import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClinicInfo, BookingConfirmationRequest } from '@/types/clinic';
import { BookingApi } from '@/utils/api';
import { useToastContext } from '@/contexts/ToastContext';
import { ProgressBar } from '@/components/ProgressBar';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';

export function EstimatedBalancePage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToastContext();
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCleaningOption, setSelectedCleaningOption] = useState<
    'add' | 'skip' | null
  >(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Cookie helper function
  const getCookie = (name: string) => {
    const match = document.cookie.match(
      new RegExp(
        '(?:^|; )' +
          name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') +
          '=([^;]*)'
      )
    );
    return match ? decodeURIComponent(match[1]) : undefined;
  };

  useEffect(() => {
    const fetchClinic = async () => {
      if (!clinicSlug) {
        setError('No clinic slug provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const config = await BookingApi.getClinicConfigBySlug(clinicSlug);
        const cs = (config.active_theme?.theme?.color_scheme ||
          config.active_theme?.color_scheme) as unknown as Record<
          string,
          string
        >;
        const clinicData: ClinicInfo = {
          id: String(config.location.id),
          name: config.location.name,
          slug: config.location.slug,
          description: config.ai_agent?.greeting_message || '',
          address: '',
          phone: '',
          email: '',
          website: undefined,
          theme: {
            primaryColor: cs?.primary || '#2563eb',
            secondaryColor: cs?.secondary || '#1d4ed8',
            accentColor: cs?.main || cs?.primary || '#2563eb',
            backgroundColor: '#FFFFFF',
            textColor: cs?.['text-color'] || '#1F2937',
            mainColor: cs?.primary || '#2563eb',
            gradient: (cs?.gradient as string) ?? null,
            iconStroke: cs?.['icon-stroke'] || cs?.secondary || undefined,
          },
          services: [],
          doctors: [],
          workingHours: {
            monday: { isOpen: false },
            tuesday: { isOpen: false },
            wednesday: { isOpen: false },
            thursday: { isOpen: false },
            friday: { isOpen: false },
            saturday: { isOpen: false },
            sunday: { isOpen: false },
          },
          isActive: true,
          aiAgent: config.ai_agent,
        };

        if (clinicData) {
          setClinic(clinicData);
        } else {
          setError('Clinic not found');
        }
      } catch (err) {
        setError('Error loading clinic information');
        console.error('Error fetching clinic:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClinic();
  }, [clinicSlug]);

  // Theme is initialized globally in App via ThemeInitializer

  const handleNext = async () => {
    if (selectedCleaningOption && !isConfirming) {
      setIsConfirming(true);

      try {
        // Gather all required data from cookies
        const patientName = getCookie('patient_full_name') || '';
        const patientPhone = getCookie('patient_phone_number') || '';
        const patientEmail = getCookie('patient_email') || '';
        const serviceId = getCookie('selected_service_id') || '';
        const providerId = getCookie('selected_provider_id') || '';
        const appointmentDate = getCookie('appointment_date') || '';
        const appointmentTime = getCookie('appointment_time') || '';
        const patientDob = getCookie('patient_dob') || '';
        const checkoutSessionId = getCookie('checkout_session_id') || '';

        // Validate required data
        if (
          !patientName ||
          !serviceId ||
          !providerId ||
          !appointmentDate ||
          !appointmentTime
        ) {
          showError('Missing required booking information. Please try again.');
          setIsConfirming(false);
          return;
        }

        // Prepare booking confirmation request
        const bookingData: BookingConfirmationRequest = {
          patient_name: patientName,
          patient_phone: patientPhone,
          patient_email: patientEmail,
          service_id: parseInt(serviceId, 10),
          provider_id: parseInt(providerId, 10),
          appointment_date: appointmentDate,
          appointment_time: appointmentTime,
          checkout_session_id: checkoutSessionId,
          preferred_language: 'en',
          notes:
            selectedCleaningOption === 'add'
              ? 'Patient requested complimentary cleaning'
              : '',
          patient_dob: patientDob,
        };

        // Call confirm booking API
        try {
          const result = await BookingApi.confirmBooking(
            clinicSlug!,
            bookingData
          );
          if (result && result.data) {
            const d = result.data;
            // Store confirmation data in cookies for the confirmation page
            document.cookie = `appointment_id=${encodeURIComponent(d.appointment_id)}; path=/; max-age=604800`;
            document.cookie = `confirmation_number=${encodeURIComponent(d.confirmation_number)}; path=/; max-age=604800`;
            document.cookie = `appointment_service=${encodeURIComponent(d.appointment_details.service)}; path=/; max-age=604800`;
            document.cookie = `appointment_provider=${encodeURIComponent(d.appointment_details.provider)}; path=/; max-age=604800`;
            document.cookie = `appointment_date_confirmed=${encodeURIComponent(d.appointment_details.date)}; path=/; max-age=604800`;
            document.cookie = `appointment_time_confirmed=${encodeURIComponent(d.appointment_details.time)}; path=/; max-age=604800`;
            document.cookie = `amount_paid=${encodeURIComponent(d.appointment_details.amount_paid)}; path=/; max-age=604800`;

            showSuccess('Your appointment has been confirmed!');

            // Navigate to appointment confirmation page
            navigate(`/${clinicSlug}/appointment-confirmation`);
          } else {
            showError('Failed to confirm appointment. Please try again.');
          }
        } catch (e) {
          showError('Failed to confirm appointment. Please try again.');
        }
      } catch (error) {
        console.error('Error confirming booking:', error);
        showError(
          'An error occurred while confirming your appointment. Please try again.'
        );
      } finally {
        setIsConfirming(false);
      }
    }
  };

  const isFormValid = () => {
    return selectedCleaningOption !== null;
  };

  if (loading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className='h-screen w-screen overflow-hidden relative flex flex-col'>
      {/* Header with Logo */}
      <div className='flex-shrink-0 p-6'>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 flex flex-col justify-center px-6 py-8'>
        <div className='max-w-3xl mx-auto w-full'>
          {/* Progress Bar and Navigation */}
          <div className='flex flex-row justify-center items-center gap-4'>
            <div className='flex items-center justify-between'>
              <button
                onClick={() => navigate(`/${clinicSlug}/patient-info`)}
                className='text-gray-600 hover:text-gray-800 flex items-center space-x-2'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  stroke='var(--clinic-icon-stroke)'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 19l-7-7 7-7'
                  />
                </svg>
                <span>Prev</span>
              </button>
            </div>

            {/* Progress Bar */}
            <ProgressBar progress={90} animated={false} />
          </div>

          {/* Cost Information Card */}
          <div
            className='rounded-2xl p-8 mb-8 mt-8 lg:mx-20'
            style={{ backgroundColor: 'var(--clinic-primary)' }}
          >
            <div className='flex items-start space-x-6'>
              {/* Confetti Icon */}
              <div className='flex-shrink-0'>
                <div
                  className='text-2xl rounded-full p-4 flex items-center justify-center'
                  style={{
                    backgroundColor: clinic?.theme.backgroundColor || '#FFFFFF',
                    opacity: 0.2,
                  }}
                >
                  🎉
                </div>
              </div>

              {/* Cost Information */}
              <div className='flex-1'>
                <h2 className='text-3xl font-semibold text-white'>
                  Good news!
                </h2>
              </div>
            </div>
            <div>
              <p className='text-white text-xl font-normal my-4'>
                For your selected appointment, your estimated out-of-pocket cost
                is:
              </p>
              <div
                className='text-3xl font-semibold mb-4'
                style={{ color: clinic?.theme.backgroundColor || '#FFFFFF' }}
              >
                $45.00
              </div>
              <div
                className='w-full h-[4px]'
                style={{
                  backgroundColor: clinic?.theme.backgroundColor || '#FFFFFF',
                  opacity: 0.3,
                }}
              ></div>
            </div>
          </div>

          {/* Choice Buttons */}
          <div className='grid md:grid-cols-2 gap-6 mb-8 lg:mx-20'>
            {/* Add Cleaning Button */}
            <button
              onClick={() => setSelectedCleaningOption('add')}
              className={`p-6 rounded-2xl shadow-lg transition-all duration-300 flex items-center space-x-4 ${
                selectedCleaningOption === 'add'
                  ? 'border-2'
                  : 'bg-white border-2 border-gray-200 hover:border-gray-300'
              }`}
              style={
                selectedCleaningOption === 'add'
                  ? {
                      backgroundColor: 'var(--clinic-primary)',
                      borderColor: 'var(--clinic-primary)',
                    }
                  : {}
              }
            >
              <div
                className={`w-12 min-w-12 h-12 min-h-12 rounded-full flex items-center justify-center`}
                style={{
                  backgroundColor: clinic?.theme.mainColor || '#20B2AA',
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M20 5.99988L9 16.9999L4 11.9999'
                    stroke={
                      selectedCleaningOption === 'add'
                        ? clinic?.theme.iconStroke ||
                          'var(--clinic-icon-stroke)'
                        : clinic?.theme.iconStroke ||
                          'var(--clinic-icon-stroke)'
                    }
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='text-left'>
                <p className={`text-lg font-medium text-gray-900 `}>
                  Yes, add my complimentary cleaning
                </p>
              </div>
            </button>

            {/* Skip Cleaning Button */}
            <button
              onClick={() => setSelectedCleaningOption('skip')}
              className={`p-6 rounded-2xl shadow-lg transition-all duration-300 flex items-center space-x-4 ${
                selectedCleaningOption === 'skip'
                  ? 'border-2'
                  : 'bg-white border-2 border-gray-200 hover:border-gray-300'
              }`}
              style={
                selectedCleaningOption === 'skip'
                  ? {
                      backgroundColor: 'var(--clinic-primary)',
                      borderColor: 'var(--clinic-primary)',
                    }
                  : {}
              }
            >
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center`}
                style={{
                  backgroundColor: clinic?.theme.mainColor || '#20B2AA',
                }}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M18 5.99988L6 17.9999M6 5.99988L18 17.9999'
                    stroke={
                      clinic?.theme.iconStroke || 'var(--clinic-icon-stroke)'
                    }
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='text-left'>
                <p className={`text-lg font-medium text-gray-900 `}>
                  I'll skip for now
                </p>
              </div>
            </button>
          </div>

          {/* Next Button */}
          <div className='flex justify-center'>
            <button
              disabled={!isFormValid() || isConfirming}
              onClick={handleNext}
              className={`h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5 ${
                isFormValid() && !isConfirming
                  ? 'text-white shadow-[var(--shadow-xs)] font-semibold hover:opacity-90'
                  : 'bg-[var(--color-gray-100)] text-[var(--color-gray-500)] shadow-[var(--shadow-xs)] font-normal cursor-not-allowed'
              }`}
              style={
                isFormValid() && !isConfirming
                  ? { backgroundColor: 'var(--clinic-primary)' }
                  : {}
              }
            >
              {isConfirming ? (
                <>
                  <LoadingSpinner size='sm' text='' />
                  Confirming...
                </>
              ) : (
                'Next'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer mobileColumnLayout={true} />
    </div>
  );
}
